<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  fetchFunilLabelUsage,
  deleteFunilLabel,
} from 'dashboard/api/aceleres/funilLabels';
import NextButton from 'dashboard/components-next/button/Button.vue';

/**
 * Aceleres: confirmar antes de apagar uma etiqueta.
 *
 * O numero de conversas nao e enfeite. Apagar remove a etiqueta de TODAS as
 * conversas que a carregam, e nao apenas da que esta aberta — sem esse numero
 * o gestor confirma achando que mexe so no que tem a frente.
 */
const props = defineProps({
  label: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['deleted', 'close']);

const { t } = useI18n();
const BASE = 'CONTACT_PANEL.LABELS.LABEL_SELECT';

const usage = ref(undefined);
const isDeleting = ref(false);
const errorMessage = ref('');

const warning = computed(() => {
  if (usage.value === undefined) return '';
  // Sem contagem o aviso fica generico, mas continua verdadeiro.
  if (usage.value === null) return t(`${BASE}.DELETE_UNKNOWN`);
  if (usage.value === 0) return t(`${BASE}.DELETE_NONE`);
  return t(`${BASE}.DELETE_IN_USE`, usage.value, { count: usage.value });
});

onMounted(async () => {
  try {
    usage.value = await fetchFunilLabelUsage(props.label.id);
  } catch (error) {
    usage.value = null;
  }
});

const confirm = async () => {
  if (isDeleting.value) return;

  isDeleting.value = true;
  errorMessage.value = '';

  try {
    const conversations = await deleteFunilLabel(props.label.id);
    emit('deleted', { title: props.label.title, conversations });
  } catch (error) {
    errorMessage.value = error.message || t(`${BASE}.DELETE_ERROR`);
  } finally {
    isDeleting.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-auto p-8 overflow-auto">
    <h2 class="text-lg font-medium text-n-slate-12">
      {{ t(`${BASE}.DELETE_TITLE`, { name: label.title }) }}
    </h2>

    <p class="mt-2 min-h-5 text-sm text-n-slate-11" aria-live="polite">
      {{ warning }}
    </p>

    <span v-if="errorMessage" class="mt-3 text-xs text-n-ruby-11">
      {{ errorMessage }}
    </span>

    <div class="flex items-center justify-end w-full gap-2 mt-7">
      <NextButton
        faded
        slate
        type="button"
        :label="t(`${BASE}.CANCEL`)"
        @click="emit('close')"
      />
      <NextButton
        ruby
        type="button"
        :label="t(`${BASE}.DELETE_CONFIRM`)"
        :is-loading="isDeleting"
        :disabled="usage === undefined"
        @click="confirm"
      />
    </div>
  </div>
</template>
